import React from 'react';
import { createPortal } from 'react-dom';
import { Search2LineIconV7, ArrowDownSLineIconV7 } from '../../ui/figma-icons-v7';
import { SelectWithLabel } from '../../ui/universal-select';
import { ColumnFilters } from '../types';
import { useBrandingColors } from '../../../hooks/useBrandingColors';

interface ColumnFiltersDropdownProps {
	isOpen: boolean;
	localFilters: ColumnFilters;
	onLocalFiltersChange: (filters: ColumnFilters) => void;
	onApplyFilters: (filters: ColumnFilters) => void;
	onClose: () => void;
}

export const ColumnFiltersDropdown: React.FC<ColumnFiltersDropdownProps> = ({
	isOpen,
	localFilters,
	onLocalFiltersChange,
	onApplyFilters,
	onClose
}) => {
	const colors = useBrandingColors();
	const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);
	const containerRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		if (isOpen && containerRef.current) {
			const parent = containerRef.current.parentElement;
			if (parent) {
				const rect = parent.getBoundingClientRect();
				setPosition({
					top: rect.bottom + 8,
					left: rect.left
				});
			}
		}
	}, [isOpen]);

	if (!isOpen) return (
		<div className="relative" ref={containerRef} />
	);

	const dropdownContent = (
		<>
			{/* Backdrop to prevent overflow clipping */}
			<div className="fixed inset-0 z-[99998]" onClick={onClose} />
			<div
				data-dropdown="column-filters"
				className="fixed w-[250px] bg-white rounded-xl border border-border shadow-[0px_4px_12px_0px_rgba(0,0,0,0.08)] z-[99999] p-3"
				style={position ? {
					top: `${position.top}px`,
					left: `${position.left}px`
				} : {}}
			>
				<div className="flex flex-col gap-3">
					<div className="flex items-center justify-between">
						<span className="text-sm font-medium text-foreground">Фильтры</span>
						<button
							onClick={onClose}
							className="text-muted-foreground hover:text-foreground transition-colors p-1"
						>
							<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 12 12">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3L3 9M3 3l6 6" />
							</svg>
						</button>
					</div>
					
					{/* Name filter */}
					<div className="flex flex-col gap-1">
						<label className="text-xs font-medium text-foreground">Имя кандидата</label>
						<div className="relative group">
							<Search2LineIconV7 className="absolute left-2 top-1/2 transform -translate-y-1/2 pointer-events-none w-3 h-3 text-muted-foreground" />
							<div className="absolute border border-border border-solid inset-0 pointer-events-none rounded-md group-focus-within:border-[var(--brand-primary,#e16349)]" />
							<input
								type="text"
								value={localFilters.name}
								onChange={(e) => onLocalFiltersChange({ ...localFilters, name: e.target.value })}
								placeholder="Поиск по имени..."
								className="w-full pl-7 pr-2.5 py-1.5 text-xs bg-input border-none rounded-md focus:outline-none"
								style={{ fontSize: '12px', lineHeight: '16px' }}
							/>
						</div>
					</div>
					
					{/* Status filter */}
					<div className="flex flex-col gap-1">
						<SelectWithLabel
							label="Статус"
							value={localFilters.status}
							onChange={(value) => onLocalFiltersChange({ ...localFilters, status: String(value) })}
							options={[
								{ value: 'all', label: 'Все статусы' },
								{ value: 'screening', label: 'Скрининг' },
								{ value: 'interview', label: 'Интервью' },
								{ value: 'offer', label: 'Предложение' },
								{ value: 'rejected', label: 'Отклонен' },
							]}
							className="text-xs"
						/>
					</div>
					
					{/* Rating filter */}
					<div className="flex flex-col gap-1">
						<SelectWithLabel
							label="Рейтинг"
							value={localFilters.rating}
							onChange={(value) => onLocalFiltersChange({ ...localFilters, rating: String(value) })}
							options={[
								{ value: 'all', label: 'Все рейтинги' },
								{ value: 'high', label: 'Высокий (7+)' },
								{ value: 'medium', label: 'Средний (5-6.9)' },
								{ value: 'low', label: 'Низкий (<5)' },
							]}
							className="text-xs"
						/>
					</div>
					
					{/* Date filter */}
					<div className="flex flex-col gap-1">
						<SelectWithLabel
							label="Дата"
							value={localFilters.date}
							onChange={(value) => onLocalFiltersChange({ ...localFilters, date: String(value) })}
							options={[
								{ value: 'all', label: 'Все даты' },
								{ value: 'today', label: 'Сегодня' },
								{ value: 'week', label: 'Неделя' },
								{ value: 'month', label: 'Месяц' },
							]}
							className="text-xs"
						/>
					</div>

					{/* Actions */}
					<div className="flex gap-2 pt-1.5 border-t border-border">
						<button
							type="button"
							onClick={(e) => {
								e.preventDefault();
								const resetFilters = { name: "", status: "all", rating: "all", date: "all" };
								onLocalFiltersChange(resetFilters);
								onApplyFilters(resetFilters);
							}}
							className="flex-1 px-2 py-1 text-xs font-medium text-muted-foreground bg-muted rounded-md hover:bg-muted/80 transition-colors cursor-pointer"
						>
							Сброс
						</button>
						<button
							type="button"
							onClick={(e) => {
								e.preventDefault();
								onApplyFilters(localFilters);
								onClose();
							}}
							className="flex-1 px-2 py-1 text-xs font-medium text-white rounded-md transition-colors cursor-pointer"
							style={{ backgroundColor: colors.primary }}
							onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primaryHover; }}
							onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = colors.primary; }}
						>
							OK
						</button>
					</div>
				</div>
			</div>
		</>
	);

	return (
		<>
			<div className="relative" ref={containerRef} />
			{createPortal(dropdownContent, document.body)}
		</>
	);
}; 